import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import ArticleGrid from './components/ArticleGrid';
import FeaturedInsight from './components/FeaturedInsight';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import { Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { fetchArticles, fetchArticleBySlug } from './lib/sanity/fetchArticles';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import Disclaimer from './pages/legal/Disclaimer';
import NotFound from './pages/NotFound';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { urlFor } from './lib/sanity/client';
import { PortableText } from '@portabletext/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

function AllArticlesPage({ articles }: { articles: any[] }) {
  //const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Add useEffect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  //const filteredArticles = articles.filter(
    //(article) =>
      //article.title.toLowerCase().includes(search.toLowerCase()) ||
      //(article.excerpt || '').toLowerCase().includes(search.toLowerCase())
  //);
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 pt-12 pb-4">
        <button
          onClick={() => navigate('/')}
          className="mb-8 px-6 py-2 rounded-full border-2 border-blue-900 text-blue-900 font-bold hover:bg-blue-900 hover:text-white transition-colors shadow"
        >
          ← Back to Home
        </button>
       
      </div>
      
      <ArticleGrid articles={articles} showAll={true} hideHeading={true} />
      <ContactSection />
      <Footer />
    </div>
  );
}

function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    fetchArticleBySlug(slug).then((data) => {
      setArticle(data);
      setLoading(false);
    });
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!article) return <div className="min-h-screen flex items-center justify-center">Article not found.</div>;

  const components = {
    block: {
      h1: ({children}: any) => <h1 className="text-4xl md:text-5xl font-serif font-bold text-blue-900 mb-6">{children}</h1>,
      h2: ({children}: any) => <h2 className="text-3xl md:text-4xl font-serif font-bold text-blue-900 mb-5">{children}</h2>,
      h3: ({children}: any) => <h3 className="text-2xl md:text-3xl font-serif font-bold text-blue-900 mb-4">{children}</h3>,
      h4: ({children}: any) => <h4 className="text-xl md:text-2xl font-serif font-bold text-blue-900 mb-3">{children}</h4>,
      normal: ({children}: any) => <p className="text-gray-700 leading-relaxed font-sans text-base md:text-lg mb-4">{children}</p>,
    },
    marks: {
      strong: ({children}: any) => <strong className="font-bold">{children}</strong>,
      em: ({children}: any) => <em className="italic">{children}</em>,
      code: ({children}: any) => <code className="bg-gray-100 rounded px-1 py-0.5 font-mono text-sm">{children}</code>,
    },
    list: {
      bullet: ({children}: any) => <ul className="list-disc list-inside mb-4 text-gray-700">{children}</ul>,
      number: ({children}: any) => <ol className="list-decimal list-inside mb-4 text-gray-700">{children}</ol>,
    },
    types: {
      image: ({value}: any) => {
        if (!value?.asset?._ref) {
          return null;
        }
        return (
          <div className="my-8">
            <img
              src={urlFor(value).width(800).height(600).fit('max').url()}
              alt={value.alt || 'Article image'}
              className="w-full h-auto rounded-lg shadow-lg"
            />
            {value.caption && (
              <p className="text-sm text-gray-600 text-center mt-2 italic">{value.caption}</p>
            )}
          </div>
        );
      },
    },
  };

  // Open Graph/Twitter meta tags
  const imageUrl = article.coverImage ? urlFor(article.coverImage).width(1200).height(630).fit('crop').format('jpg').url() : 'https://via.placeholder.com/1200x630/1e40af/ffffff?text=Big+Deal+Making';
  const pageUrl = `https://www.bigdealmaking.site/articles/${slug}`;
  
  // Debug logging
  console.log('Article meta tags:', {
    title: article.title,
    excerpt: article.excerpt,
    imageUrl,
    pageUrl,
    hasCoverImage: !!article.coverImage,
    coverImageData: article.coverImage
  });

  // Log the actual meta tags being generated
  console.log('Generated meta tags:', {
    'og:image': imageUrl,
    'twitter:image': imageUrl,
    'og:title': article.title,
    'og:description': article.excerpt || 'Read this insightful article on Big Deal Making'
  });

  // Test image URL accessibility
  if (imageUrl && !imageUrl.includes('via.placeholder.com')) {
    fetch(imageUrl, { method: 'HEAD' })
      .then(response => {
        console.log('Image URL accessibility:', {
          url: imageUrl,
          status: response.status,
          accessible: response.ok,
          headers: Object.fromEntries(response.headers.entries())
        });
      })
      .catch(error => {
        console.error('Image URL error:', {
          url: imageUrl,
          error: error.message
        });
      });
  }

  // Also test with a direct image request
  if (imageUrl && !imageUrl.includes('via.placeholder.com')) {
    const img = new Image();
    img.onload = () => {
      console.log('Image loaded successfully:', {
        url: imageUrl,
        width: img.width,
        height: img.height
      });
    };
    img.onerror = () => {
      console.error('Image failed to load:', imageUrl);
    };
    img.src = imageUrl;
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | Big Deal Making</title>
        <meta name="description" content={article.excerpt || 'Read this insightful article on Big Deal Making'} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt || 'Read this insightful article on Big Deal Making'} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Big Deal Making" />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content={article.title} />
        
        {/* Twitter Card - More specific for better compatibility */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bigdealmaking" />
        <meta name="twitter:creator" content="@bigdealmaking" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt || 'Read this insightful article on Big Deal Making'} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={article.title} />
        
        {/* Additional meta tags for better compatibility */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content={article.author || 'Big Deal Making'} />
        {article.publishedAt && (
          <meta property="article:published_time" content={new Date(article.publishedAt).toISOString()} />
        )}
      </Helmet>
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 pt-12 pb-16">
          <button
            onClick={() => navigate('/articles')}
            className="mb-8 px-6 py-2 rounded-full border-2 border-blue-900 text-blue-900 font-bold hover:bg-blue-900 hover:text-white transition-colors shadow"
          >
            ← Back to Articles
          </button>
          
          {/* Article Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-blue-900 leading-tight">{article.title}</h1>
            <div className="flex items-center text-gray-600 space-x-4 mb-8">

              {/* start share button coder */}
              <div className="flex gap-4 mb-4">
              {/* LinkedIn Share */}
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(article.title)}&summary=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:text-blue-900 underline"
              >
                Share on LinkedIn
              </a>

              {/* WhatsApp Share */}
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${article.title} ${pageUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 underline"
              >
                Share on WhatsApp
              </a>

              {/* X (Twitter) Share */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(pageUrl)}&via=bigdealmaking`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-600 underline"
              >
                Share on X
              </a>
            </div>
              {/* end share button code */}
              
              <span className="font-medium">{article.author}</span>
              <span>•</span>
              <span>{article.publishedAt && new Date(article.publishedAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>

          {/* Cover Image */}
          {article.coverImage && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={urlFor(article.coverImage).width(1600).height(900).url()} 
                alt={article.title} 
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="prose prose-lg md:prose-xl max-w-none">
            {article.body && <PortableText value={article.body} components={components} />}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

function LegalPage({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 pt-12 pb-4">
        <button
          onClick={() => navigate('/')}
          className="mb-8 px-6 py-2 rounded-full border-2 border-blue-900 text-blue-900 font-bold hover:bg-blue-900 hover:text-white transition-colors shadow"
        >
          ← Back to Home
        </button>
      </div>
      {children}
      <Footer />
    </div>
  );
}

function App() {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles().then((data) => {
      setArticles(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <HelmetProvider>
      <SpeedInsights />
      <Routes>
        <Route path="/" element={
          <div className="min-h-screen bg-white">
            <Header />
            <ArticleGrid articles={articles.slice(0, 3)} />
            <FeaturedInsight />
            <ContactSection />
            <Footer />
          </div>
        } />
        <Route path="/articles" element={<AllArticlesPage articles={articles} />} />
        <Route path="/articles/:slug" element={<ArticleDetailPage />} />
        <Route path="/privacy-policy" element={
          <LegalPage>
            <PrivacyPolicy />
          </LegalPage>
        } />
        <Route path="/terms-of-service" element={
          <LegalPage>
            <TermsOfService />
          </LegalPage>
        } />
        <Route path="/disclaimer" element={
          <LegalPage>
            <Disclaimer />
          </LegalPage>
        } />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </HelmetProvider>
  );
}

export default App;
